import React, { useState } from "react";
import { applyOssLint } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useTranslation } from "react-i18next";

/**
 * v0.4.37 Task B (ALL DEFAULTS, operator sign-off 2026-06-04).
 *
 * Read + EDIT modal for OSS lint offenders. ADMIN sees a per-row
 * Edit Rate dropdown with the destination country's canonical
 * rate types. Pick + Apply All sends the batch to
 * `POST /api/ossfilings/{calc_id}/lint/apply`. Response carries
 * the fresh lint payload (B8=a) so the modal re-renders without
 * a manual refresh. OPERATORs see the existing read-only view.
 *
 * 409 CALC_FROZEN is surfaced inline so the admin can see that
 * the calc can no longer be amended (frozen calcs are immutable).
 */
export const OssLintReviewModal = ({
  open, offenders, calcId, calcFrozen, onClose, onLintUpdated,
}) => {
  const { admin } = useAuth();
  const { t } = useTranslation();
  const isAdmin = admin?.role === "ADMIN";
  const [picks, setPicks] = useState({}); // {row_id: rate_type}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  if (!open) return null;
  const canEdit = isAdmin && !calcFrozen;
  const picksList = Object.entries(picks).filter(([, v]) => !!v);

  const handleApply = async () => {
    setBusy(true); setError(null); setLastResult(null);
    try {
      const corrections = picksList.map(([id, rate_type]) => ({ id, rate_type }));
      const r = await applyOssLint(calcId, corrections);
      setLastResult(r);
      setPicks({});
      if (r?.lint && onLintUpdated) onLintUpdated(r.lint);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        setError(detail.message || detail.error || "Apply failed");
      } else {
        setError(detail || e.message || "Apply failed");
      }
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="oss-lint-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 dark:bg-slate-900/90 backdrop-blur"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 border border-amber-700/60 rounded-xl max-w-5xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-amber-700/40 flex items-center justify-between gap-3">
          <div className="flex-1">
            <div className="text-xs text-amber-700 dark:text-amber-300">
              {t("ui.oss_lint.title", { defaultValue: "OSS rate sanity check" })}
            </div>
            <div className="text-[11px] text-slate-600 dark:text-slate-300 mt-0.5">
              {t("ui.oss_lint.subtitle", { count: offenders.length, defaultValue: `${offenders.length} row(s) won't resolve to a canonical OSS VATRateType.` })} {canEdit ? t("ui.oss_lint.hint_pick", { defaultValue: "Pick a rate per row and Apply All." }) : (calcFrozen ? t("ui.oss_lint.hint_frozen", { defaultValue: "Calc is frozen — corrections disabled." }) : t("ui.oss_lint.hint_admin", { defaultValue: "ADMIN role required to apply corrections." }))}
            </div>
          </div>
          {canEdit && (
            <button
              type="button"
              data-testid="oss-lint-apply-all"
              data-resolution-class="self-resolve"
              data-resolution-endpoint="POST /api/ossfilings/{calc_id}/lint/apply"
              disabled={busy || picksList.length === 0}
              onClick={handleApply}
              className="text-xs px-3 py-2 rounded-xl bg-teal-600 hover:bg-teal-700 disabled:bg-slate-200 disabled:text-slate-500 dark:text-slate-400 text-white font-bold"
            >
              {busy ? t("ui.oss_lint.applying", { defaultValue: "Applying…" }) : t("ui.oss_lint.apply_n", { count: picksList.length, defaultValue: `Apply ${picksList.length}` })}
            </button>
          )}
          <button
            type="button"
            data-testid="oss-lint-modal-close"
            onClick={onClose}
            className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 text-xs"
          >{t("ui.oss_lint.close", { defaultValue: "close ✕" })}</button>
        </div>

        {error && (
          <div
            data-testid="oss-lint-error"
            className="mx-5 mt-3 text-[11px] rounded-xl border border-rose-700/60 bg-rose-500/10 text-rose-700 dark:text-rose-300 px-3 py-2"
          >{error}</div>
        )}
        {lastResult && (
          <div
            data-testid="oss-lint-applied-summary"
            className="mx-5 mt-3 text-[11px] rounded-xl border border-teal-700/60 bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 px-3 py-2"
          >
            {t("ui.oss_lint.applied", { applied: lastResult.applied_count, remaining: lastResult.lint.offender_count, defaultValue: `Applied ${lastResult.applied_count} · ${lastResult.lint.offender_count} offender(s) remaining` })}{lastResult.errors?.length ? t("ui.oss_lint.applied_errors", { count: lastResult.errors.length, defaultValue: ` · ${lastResult.errors.length} error(s)` }) : ""}.
          </div>
        )}

        <div className="overflow-auto flex-1">
          <table className="w-full text-[11px]">
            <thead className="bg-white dark:bg-slate-900 text-amber-700 dark:text-amber-300 tracking-normal">
              <tr>
                <th className="text-left px-3 py-2">{t("ui.oss_lint.col_order_ref", { defaultValue: "Order ref" })}</th>
                <th className="text-left px-3 py-2">{t("ui.oss_lint.col_date", { defaultValue: "Date" })}</th>
                <th className="text-left px-3 py-2">{t("ui.oss_lint.col_from_to", { defaultValue: "From → To" })}</th>
                <th className="text-right px-3 py-2">{t("ui.oss_lint.col_gross", { defaultValue: "Gross" })}</th>
                <th className="text-right px-3 py-2">{t("ui.oss_lint.col_declared", { defaultValue: "Declared" })}</th>
                <th className="text-left px-3 py-2">{t("ui.oss_lint.col_reason", { defaultValue: "Reason" })}</th>
                <th className="text-left px-3 py-2">{t("ui.oss_lint.col_pick", { defaultValue: "Pick" })}</th>
              </tr>
            </thead>
            <tbody>
              {offenders.map((o) => (
                <OffenderRow
                  key={o.id}
                  offender={o}
                  pick={picks[o.id] || ""}
                  onPick={(rt) => setPicks((p) => ({ ...p, [o.id]: rt }))}
                  canEdit={canEdit}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const OffenderRow = ({ offender: o, pick, onPick, canEdit }) => {
  const { t } = useTranslation();
  const opts = Object.entries(o.expected_rate_options || {});
  const hasOpts = opts.length > 0 && !opts[0][0].startsWith("_");
  return (
    <tr
      data-testid={`oss-lint-row-${o.id}`}
      className="border-t border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400"
    >
      <td className="px-3 py-2 text-amber-700 dark:text-amber-300">{o.order_ref}</td>
      <td className="px-3 py-2">{String(o.transaction_date || "").slice(0, 10)}</td>
      <td className="px-3 py-2">{o.departure} → {o.arrival}</td>
      <td className="px-3 py-2 text-right">{o.gross_amount}</td>
      <td className="px-3 py-2 text-right text-rose-700 dark:text-rose-300">{o.declared_rate}</td>
      <td className="px-3 py-2 text-rose-700 dark:text-rose-300">{o.reason}</td>
      <td className="px-3 py-2">
        {canEdit && hasOpts ? (
          <select
            data-testid={`oss-lint-pick-${o.id}`}
            value={pick}
            onChange={(e) => onPick(e.target.value)}
            className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 rounded px-2 py-1 text-[11px]"
          >
            <option value="">{t("ui.oss_lint.choose_rate", { defaultValue: "— choose rate —" })}</option>
            {opts.map(([rt, v]) => (
              <option key={rt} value={rt}>{rt} ({v})</option>
            ))}
          </select>
        ) : (
          <span className="text-slate-500 dark:text-slate-400">
            {opts.map(([k, v]) => `${k}=${v}`).join(", ")}
          </span>
        )}
      </td>
    </tr>
  );
};
